import { Injectable } from '@angular/core';
import { LoggerService } from '../../../core/services/logger.service';
import { UtilService } from '../../../core/services/util.service';
import { ICRMPF } from 'src/app/components/pessoa-fisica/interfaces/ICRMPF';

import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import { AddUserIdOnAttachDataCommand } from 'src/app/components/pessoa-fisica/domain/CRM/AddAttachDataCommand';
import { PFSessionDataDTO } from 'src/app/data/Dynamics/pessoa-fisica/PFSessionDataDTO';
import { TicketCreationRequestControlService } from 'src/app/shared/services/ticket-creation-request-control.service';
import {
  CIFDatadogService,
  CloseTicketByEventProps,
} from 'src/app/shared/services/cif-datadog';
import { quickWebLogger } from '@quickweb/logger';
import { MediaType } from 'src/app/utils/media-type';
import { isDigital } from 'src/app/utils/cxone-utils';
import { JsonConverterUtils } from '../../CXOne/util/JsonConverterUtils';
import { LocalStorageUtils } from '../../CXOne/util/LocalStorageUtils';

export type Ticket = { id_interacao: string | null; id_ticket: string | null };
@Injectable({
  providedIn: 'root',
})
export class DynamicsPessoaFisica implements ICRMPF {
  service: string = 'DYNAMICS PF';
  customerIdNid: string = '94178430-fbd1-ec11-a7b6-002248385269';
  public onAddUserIdOnAttachData$ = new Subject<AddUserIdOnAttachDataCommand>();
  private messageHandler: (event: Event) => void;

  constructor(
    private logger: LoggerService,
    private utilService: UtilService,
    private ticketCreationRequestControlService: TicketCreationRequestControlService,
    private cifDatadogService: CIFDatadogService,
    private jsonConverterUtils: JsonConverterUtils,
    private localStorageUtils: LocalStorageUtils,
  ) {
    this.messageHandler = this.DynamicsMessageHandler.bind(this);
    window.addEventListener('message', this.messageHandler);
  }

  initialize(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let cifUrl = environment.cif_versao2_pf;
      this.logger.info(`${this.service} - inicializando CIF: ${cifUrl}`);
      const script = this.createScriptElement(cifUrl);
      document.body.appendChild(script);
      const onCIFInitDone = async (event: Event) => {
        try {
          await this.configureCIF();
          this.logger.info(`${this.service} - CIF inicializado com sucesso`);

          resolve();
        } catch (error) {
          if (error instanceof Error && error.message) {
            reject(error);
          } else {
            this.logger.error(
              `${this.service} - Erro ao criar sessão: `,
              error,
            );
            //this.logger.flushLogError();
            reject(
              new Error(
                `${this.service} - Erro ao criar sessão: ${error instanceof Error ? error.message : error}`,
              ),
            );
          }
        } finally {
          window.removeEventListener('CIFInitDone', onCIFInitDone);
        }
      };
      window.addEventListener('CIFInitDone', onCIFInitDone);
    });
  }

  public async openTicket(
    contactCenterInteractionId: string,
    attachData: any,
    isFocused: boolean,
    masterId: string,
    mediaType: MediaType,
  ): Promise<void> {
    var uJSON = attachData.U_JSON;

    this.cifDatadogService.openTicket.initialData({
      attachData,
      masterId,
      mediaType,
    });

    try {
      uJSON = JSON.parse(uJSON);
      uJSON = this.utilService.keysToLowerCase(uJSON);
      uJSON = this.jsonConverterUtils.JsonStringfy(uJSON);
    } catch (e) {
      this.logger.error('Erro ao parsear attachData U_JSON: ', e);
      uJSON = this.jsonConverterUtils.JsonStringfy({});

      this.cifDatadogService.openTicket.invalidJsonParser({
        attachData,
        masterId,
        mediaType,
        error: e,
      });

      //log error
    }

    if (isDigital(mediaType)) {
      const ticketHasRequest =
        this.ticketCreationRequestControlService.checkTicketRequest(masterId);

      if (ticketHasRequest === true) {
        this.logger.info(
          'DYNAMICS - Master ID já inserido na lista, ',
          masterId,
        );
        this.cifDatadogService.openTicket.request.duplicated({
          masterId: masterId,
          attachData: attachData,
          mediaType,
        });
        return;
      }
      this.ticketCreationRequestControlService.saveTicketRequest(masterId);
      this.ticketCreationRequestControlService.clearUpLastTicketCreationRequest();
    }

    this.logger.info(
      `${this.service} - Abrindo ticket com attachData: `,
      uJSON,
    );

    const interactionId = await this.CreateInteraction(
      uJSON,
      mediaType,
      contactCenterInteractionId,
    );
    const ticketId = await this.RetrieveTicketId(
      interactionId,
      mediaType,
      masterId,
    );

    const sessionId = await this.CreateSession(
      interactionId,
      ticketId,
      isFocused,
      mediaType,
      masterId,
    );

    const sessionData: PFSessionDataDTO = {
      contactCenterInteractionId: contactCenterInteractionId,
      dynamicsSessionId: sessionId,
      dynamicsinteractionId: interactionId,
      isSac: this.isOperationSac(uJSON),
      timeAfterCallWork: this.GetTimeoutAfterCallWorkByAttachData(uJSON),
    };

    this.addSessionRelationship(sessionData);
    this.SendCustomerIdToContactCenter(contactCenterInteractionId, ticketId);
  }

  private isOperationSac(attachData: any): boolean {
    const attachJson = this.jsonConverterUtils.JsonParsed(attachData);
    const callbacksac = attachJson.callbacksac;
    this.logger.info('Attach Callback Sac: ', callbacksac);

    if (callbacksac === 's' || callbacksac === 'S') {
      return true;
    }
    return false;
  }

  private GetTimeoutAfterCallWorkByAttachData(attachData: any): number {
    const attachJson = this.jsonConverterUtils.JsonParsed(attachData);
    const timedefault = 45;

    const timeout = Number(attachJson.s_usac_timeout);

    if (timeout === null || timeout === undefined) {
      this.logger.warn('Usando timeout default: ', timedefault);
      return timedefault;
    }

    return timeout;
  }

  public async customerTransfered(
    ContactCenterInteractionId: string,
    attachData: Record<string, unknown>,
    mediaType: MediaType,
  ): Promise<void> {
    this.logger.info(
      `${this.service} - Transferência para o ContactCenterInteractionId: ${ContactCenterInteractionId}`,
    );

    const sessionRelationship = this.getSessionRelationship(
      ContactCenterInteractionId,
    );

    const sessionId = sessionRelationship?.dynamicsSessionId;
    const interacaoId = sessionRelationship?.dynamicsinteractionId;

    const closeEventLogDetails: CloseTicketByEventProps = {
      sessionId: sessionId,
      interactionId: interacaoId,
      attachData: attachData,
      mediaType,
      masterId: ContactCenterInteractionId,
    };

    this.cifDatadogService.closeTicket.start(
      'fecharSessao_uraTransferencia',
      closeEventLogDetails,
    );

    if (!sessionRelationship) {
      this.logger.warn(
        `${this.service} - Nenhuma sessão encontrada para o ContactCenterInteractionId: ${ContactCenterInteractionId}`,
      );

      this.cifDatadogService.closeTicket.relationshipFail(
        'fecharSessao_uraTransferencia',
        {
          masterId: ContactCenterInteractionId,
          mediaType,
          attachData,
        },
      );
      return;
    }

    try {
      this.logger.info(
        `${this.service} - Encerrando sessão (transferência): ${sessionId}, Interação: ${interacaoId}`,
      );

      await (window as any).Microsoft.CIFramework.raiseEvent(
        'fecharSessao_uraTransferencia',
        {
          sessionId: sessionId,
          correlationId: interacaoId,
        },
      );

      this.cifDatadogService.closeTicket.success(
        'fecharSessao_uraTransferencia',
        closeEventLogDetails,
      );

      this.logger.info(`${this.service} - Sessão encerrada com sucesso.`);
      //this.logger.flushLogInfo();
    } catch (error) {
      this.cifDatadogService.closeTicket.fail('fecharSessao_uraTransferencia', {
        ...closeEventLogDetails,
        error,
      });

      this.logger.error(
        `${this.service} - Erro ao encerrar ticket (transferência): `,
        error,
      );
    }

    this.removeSessionRelationship(ContactCenterInteractionId);
  }

  public async customerTransferedAuthIVR(
    ContactCenterInteractionId: string,
    attachData: Record<string, unknown>,
    mediaType: MediaType,
  ): Promise<void> {
    this.logger.info(
      `${this.service} - Trasnferindo para a ura auth para o ContactCenterInteractionId: ${ContactCenterInteractionId}`,
    );

    const sessionRelationship = this.getSessionRelationship(
      ContactCenterInteractionId,
    );
    const sessionId = sessionRelationship?.dynamicsSessionId;
    const interacaoId = sessionRelationship?.dynamicsinteractionId;

    const closeEventLogDetails: CloseTicketByEventProps = {
      sessionId: sessionId,
      interactionId: interacaoId,
      attachData: attachData,
      masterId: ContactCenterInteractionId,
      mediaType,
    };

    this.cifDatadogService.closeTicket.start(
      'fecharSessao_uraAut',
      closeEventLogDetails,
    );

    if (!sessionRelationship) {
      this.logger.warn(
        `${this.service} - Nenhuma sessão encontrada para o ContactCenterInteractionId: ${ContactCenterInteractionId}`,
      );

      this.cifDatadogService.closeTicket.relationshipFail(
        'fecharSessao_uraAut',
        {
          masterId: ContactCenterInteractionId,
          mediaType,
          attachData,
        },
      );
      return;
    }

    try {
      this.logger.info(
        `${this.service} - Encerrando sessão (transferência URA): ${sessionId}, Interação: ${interacaoId}`,
      );

      await (window as any).Microsoft.CIFramework.raiseEvent(
        'fecharSessao_uraAut',
        {
          sessionId: sessionId,
          correlationId: interacaoId,
        },
      );

      this.cifDatadogService.closeTicket.success(
        'fecharSessao_uraAut',
        closeEventLogDetails,
      );

      this.logger.info(`${this.service} - Sessão encerrada com sucesso.`);
    } catch (error) {
      this.cifDatadogService.closeTicket.fail('fecharSessao_uraAut', {
        ...closeEventLogDetails,
        error,
      });

      this.logger.error(
        `${this.service} - Erro ao encerrar ticket (ura auth): `,
        error,
      );
    }

    this.removeSessionRelationship(ContactCenterInteractionId);
  }

  public async endTicket(
    ContactCenterInteractionId: string,
    attachData: Record<string, unknown>,
    mediaType: MediaType,
  ): Promise<void> {
    this.logger.info(
      `${this.service} - Encerrando ticket para o ContactCenterInteractionId: ${ContactCenterInteractionId}`,
    );

    const sessionRelationship = this.getSessionRelationship(
      ContactCenterInteractionId,
    );

    const sessionId = sessionRelationship?.dynamicsSessionId;
    const interacaoId = sessionRelationship?.dynamicsinteractionId;

    const closeEventLogDetails: CloseTicketByEventProps = {
      sessionId: sessionId,
      interactionId: interacaoId,
      attachData: attachData,
      mediaType,
      masterId: ContactCenterInteractionId,
    };

    this.cifDatadogService.closeTicket.start(
      'fecharSessao',
      closeEventLogDetails,
    );

    if (!sessionRelationship) {
      this.logger.warn(
        `${this.service} - Nenhuma sessão encontrada para o ContactCenterInteractionId: ${ContactCenterInteractionId}`,
      );

      this.cifDatadogService.closeTicket.relationshipFail('fecharSessao', {
        masterId: ContactCenterInteractionId,
        mediaType,
        attachData,
      });
      return;
    }

    if (sessionRelationship.isSac) {
      this.logger.info(
        `${this.service} - Operação SAC identificada. Aguardando ${sessionRelationship.timeAfterCallWork} segundos antes de encerrar a sessão.`,
      );
      await this.delay(sessionRelationship.timeAfterCallWork * 1000);
    }

    try {
      this.logger.info(
        `${this.service} - Encerrando sessão: ${sessionId}, Interação: ${interacaoId}`,
      );

      await (window as any).Microsoft.CIFramework.raiseEvent('fecharSessao', {
        sessionId: sessionId,
        correlationId: interacaoId,
      });

      this.logger.info(`${this.service} - Sessão encerrada com sucesso.`);

      this.cifDatadogService.closeTicket.success(
        'fecharSessao',
        closeEventLogDetails,
      );
    } catch (error) {
      this.cifDatadogService.closeTicket.fail('fecharSessao', {
        ...closeEventLogDetails,
        error,
      });

      this.logger.error(`${this.service} - Erro ao encerrar ticket: `, error);
    }

    this.removeSessionRelationship(ContactCenterInteractionId);
  }

  private createScriptElement(cifUrl: string): HTMLScriptElement {
    const script = document.createElement('script');
    script.src = cifUrl;
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute('data-crmurl', cifUrl);
    script.setAttribute('data-cifid', 'CIFMainLibrary');
    return script;
  }

  private async configureCIF(): Promise<void> {
    try {
      await (window as any).Microsoft.CIFramework.setMode(1);
      await (window as any).Microsoft.CIFramework.addHandler(
        'onmodechanged',
        async (eventData: any) => {
          await (window as any).Microsoft.CIFramework.setMode(1);
          return Promise.resolve();
        },
      );
      await (window as any).Microsoft.CIFramework.setWidth(400);
    } catch (error) {
      this.logger.error(`${this.service} - Erro ao configurar CIF: `, error);
      //this.logger.flushLogError();
      throw new Error(`${this.service} - Erro ao configurar CIF: ${error}`);
    }
  }

  private async CreateInteraction(
    uJSON: any,
    mediaType: MediaType,
    contactId: string,
  ): Promise<string> {
    try {
      const jsonData = {
        itau_u_nomeprojeto: 'Atender PF',
        itau_mst_attachdata: uJSON,
      };

      this.logger.info(
        'Solicitando criação de interação.',
        this.jsonConverterUtils.JsonStringfy(jsonData),
      );

      this.cifDatadogService.openTicket.request.create({
        attachData: uJSON,
        mediaType,
        masterId: contactId,
      });

      let createRecordResult = await (
        window as any
      ).Microsoft.CIFramework.createRecord(
        'itau_interacao',
        this.jsonConverterUtils.JsonStringfy(jsonData),
      );

      //log Criação

      this.logger.info(
        'Criação de interação realizada com sucesso.',
        this.jsonConverterUtils.JsonParsed(createRecordResult),
      );

      this.cifDatadogService.openTicket.request.success({
        attachData: uJSON,
        mediaType,
        masterId: contactId,
      });

      return this.jsonConverterUtils.JsonParsed(createRecordResult).id;
    } catch (error) {
      this.logger.error('Erro ao criar interação.', error);
      //this.logger.flushLogError();

      this.cifDatadogService.openTicket.request.fail({
        attachData: uJSON,
        mediaType,
        masterId: contactId,
        error,
      });

      throw new Error(
        `Erro ao criar interação: ${error instanceof Error ? error.message : error}`,
      );
    }
  }

  private async RetrieveTicketId(
    idInteracao: string,
    mediaType: MediaType,
    masterId: string,
  ): Promise<string> {
    this.logger.info('Recuperando ID do Ticket para a interação.', idInteracao);
    let RECALL_LIMIT = 40;
    let RECALL_DELAY_MS = 2000;

    this.cifDatadogService.openTicket.retrieve.start({
      interactionId: idInteracao,
      mediaType,
      masterId,
    });

    var idTicket;
    for (let attempt = 0; attempt < RECALL_LIMIT; attempt++) {
      this.logger.info('Tentativa', attempt + 1);
      await this.delay(RECALL_DELAY_MS);

      try {
        var resultTicket = await (
          window as any
        ).Microsoft.CIFramework.retrieveRecord(
          'itau_interacao',
          idInteracao,
          '?$select=itau_interacaoid&$expand=itau_incident_InteracaoId_itau_interacao($select=incidentid)',
        );

        var interaction = this.jsonConverterUtils.JsonParsed(resultTicket);
        this.logger.info(
          'interaction: ',
          this.jsonConverterUtils.JsonStringfy(interaction),
        );

        if (
          interaction['itau_incident_InteracaoId_itau_interacao']?.[0] != null
        ) {
          if (
            interaction['itau_incident_InteracaoId_itau_interacao'][0]
              .incidentid != null
          ) {
            idTicket =
              interaction['itau_incident_InteracaoId_itau_interacao'][0]
                .incidentid;
            // log data info
            this.logger.info(
              `Id Ticket recuperado com sucesso da interação `,
              idTicket,
            );
            this.cifDatadogService.openTicket.retrieve.success({
              interactionId: idInteracao,
              mediaType,
              masterId,
            });

            return idTicket;
          }
        }
      } catch (e) {
        this.cifDatadogService.openTicket.retrieve.excessiveAttemptsError({
          interactionId: idInteracao,
          mediaType,
          masterId,
          attempts: attempt,
        });

        this.logger.warn('Falha na tentativa: ', attempt, e);
      }
    }
    this.cifDatadogService.openTicket.retrieve.fail({
      interactionId: idInteracao,
      mediaType,
      masterId,
    });

    throw new Error(`Erro ao recuperar ID do Ticket.`);
  }

  private async CreateSession(
    interactionId: string,
    retriveTicketId: string,
    isFocused: boolean,
    mediaType: MediaType,
    masterId: string,
  ): Promise<string> {
    var inputTemplate = {
      templateName: 'fo_session_Ticket',
      templateParameters: {
        id_interacao: interactionId,
        id_ticket: retriveTicketId,
        cliente_on: true,
      },
      isFocused: isFocused,
    };

    this.logger.info(
      'Criando sessão com template.',
      this.jsonConverterUtils.JsonStringfy(inputTemplate),
    );

    this.cifDatadogService.openTicket.session.request({
      interactionId,
      template: inputTemplate,
      mediaType,
      masterId,
    });

    try {
      const sessionId = await (
        window as any
      ).Microsoft.CIFramework.createSession(inputTemplate);

      this.cifDatadogService.openTicket.session.success({
        sessionId: sessionId,
        interactionId: interactionId,
        mediaType,
        masterId,
      });

      this.logger.info(
        `Sucesso ao criar a de sessão para a interação ${interactionId}`,
        sessionId,
      );

      return sessionId;
    } catch (error) {
      this.logger.error('Erro ao criar sessão.', error);
      //this.logger.flushLogError();
      this.cifDatadogService.openTicket.session.fail({
        interactionId,
        template: inputTemplate,
        mediaType,
        masterId,
        error,
      });

      throw new Error(
        `Erro ao criar sessão: ${error instanceof Error ? error.message : error}`,
      );
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private addSessionRelationship(sessionData: PFSessionDataDTO): void {
    this.localStorageUtils.safeSetItem(
      `sessionRelationship-${sessionData.contactCenterInteractionId}`,
      this.jsonConverterUtils.JsonStringfy(sessionData),
    );
  }

  private getSessionRelationship(
    ContactCenterInteractionId: string,
  ): PFSessionDataDTO | null {
    const value = this.localStorageUtils.safeGetItem(
      `sessionRelationship-${ContactCenterInteractionId}`,
    );
    if (value) {
      return this.jsonConverterUtils.JsonParsed(value);
    }
    return null;
  }

  private removeSessionRelationship(contactCenterInteractionId: string): void {
    this.localStorageUtils.safeRemoveItem(
      `sessionRelationship-${contactCenterInteractionId}`,
    );
  }

  private async SendCustomerIdToContactCenter(
    contactCenterInteractionId: string,
    incidentId: string,
  ): Promise<void> {
    this.logger.info(
      `${this.service} - Iniciando tentativa de identificação do cliente...`,
    );

    while (this.IsContactCenterInteractionLive(contactCenterInteractionId)) {
      let customerId =
        await this.GetCustomerIdentificationOnIncident(incidentId);

      if (customerId != null && customerId != this.customerIdNid) {
        this.logger.info(
          `${this.service} - Cliente identificado: ${customerId}`,
        );

        var command: AddUserIdOnAttachDataCommand = {
          interactionId: contactCenterInteractionId,
          crmUserId: customerId,
        };

        this.onAddUserIdOnAttachData$.next(command);
        return;
      }

      this.logger.info(`${this.service} - Cliente não identificado.`);
      this.logger.info(
        `${this.service} - Aguardando identificação do cliente...`,
      );
      await this.delay(5000);
    }

    this.logger.warn(
      `${this.service} - Interação encerrada antes da identificação do cliente. Tentativa de identificação encerrada.`,
    );
  }

  private async GetCustomerIdentificationOnIncident(
    incidentId: string,
  ): Promise<string | null> {
    try {
      var customer = JSON.parse(
        await (window as any).Microsoft.CIFramework.retrieveRecord(
          'incident',
          incidentId,
          '?$select=_customerid_value,statuscode',
        ),
      );

      var customerId = customer._customerid_value;
      this.logger.info(
        'CustomerId ',
        customerId,
        'encontrado no incidente ',
        incidentId,
      );
      return customerId;
    } catch (e) {
      this.logger.error(
        `${this.service} - Erro ao recuperar identificação do cliente: `,
        e,
      );
      return null;
    }
  }

  private IsContactCenterInteractionLive(
    contactCenterInteractionId: string,
  ): boolean {
    if (this.getSessionRelationship(contactCenterInteractionId)) {
      return true;
    }

    return false;
  }

  public async ChangeSessionFocus(
    ContactCenterInteractionId: string,
  ): Promise<void> {
    const sessionRelationship = this.getSessionRelationship(
      ContactCenterInteractionId,
    );

    await (window as any).Microsoft.CIFramework.raiseEvent(
      'ControleFocarSessao',
      {
        sessionId: sessionRelationship?.dynamicsSessionId,
        correlationId: sessionRelationship?.dynamicsinteractionId,
      },
    );
  }

  public DynamicsMessageHandler(e: any) {
    if (e.data.messageType === 'onSessionClosed') {
      this.OnSessionClosedHandlerFromWindow(e);
    }

    if (e.data.messageType === 'onSessionSwitched') {
      this.OnSessionSwitchHandlerFromWindow(e);
    }
  }
  public async OnSessionSwitchHandlerFromWindow(e: any) {
    this.logger.info(
      'Troca de sessão solicitada no dynamics via message',
      this.jsonConverterUtils.JsonStringfy(e),
    );

    if (e.data.messageData.focused === true) {
      return;
    }

    const ContactCenterInteractionId =
      this.localStorageUtils.safeGetItem('interactionFocused') ?? '';
    const sessionRelationship = this.getSessionRelationship(
      ContactCenterInteractionId,
    );

    const controleFocarSessao = {
      sessionId: sessionRelationship?.dynamicsSessionId,
      correlationId: sessionRelationship?.dynamicsinteractionId,
    };

    this.logger.info(
      'Solicitacao retorno a sessão do dynamics.',
      this.jsonConverterUtils.JsonStringfy(controleFocarSessao),
    );
    await (window as any).Microsoft.CIFramework.raiseEvent(
      'ControleFocarSessao',
      controleFocarSessao,
    );
  }

  public async OnSessionClosedHandlerFromWindow(e: any) {
    this.logger.info('Encerramento de sessão solicitada no dynamics.', e);
  }
}

// --------------------------------------------------------------------------------------
// Estimulos Ao encerrar sessão

// fo_queda_ligacao > Utilizado no encerramento da ligação (independente de quem tenha feito o encerramento cliente/operador)
// fecharSessao_uraAut > Utilizado na transferencia para ura autenticadora
// fecharSessao_uraTransferencia > utilizado nas outras transferencias

// Metodo:
//   Microsoft.CIFramework.raiseEvent("fecharSessao_uraAut", eventInputParameters);

// Objeto:
// eventInputParameters = {"sessionId": sessionId, "correlationId": idInteracao };

// sessionId => Id da sessão que foi rebida na criação da session
// idInteracao => Id da interação que foi recebido na criação da itau_interacao

// Ligação > attachData (dados da ligação)
// dados da ligação > interação > dados brutos da ligação

// Recuperar dados da organização
// Microsoft.CIFramework.getEnvironment();
